import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import './LogListItem.scss';

import { DangerAlert, InfoAlert, WaitingAlert, Table, SecondaryBtn, SvgCaretDown, SvgCaretUp } from '@tmapy/style-guide';

const rightIconDown = { element: <SvgCaretDown />, position: 'right' as const };
const rightIconUp = { element: <SvgCaretUp />, position: 'right' as const };

import type { LogResourceType } from '../../types';
import { QUERY_STATE } from '../../constants';
import { LogListItem } from './LogListItem';
import { useDocChecksLogsListResource } from '../../openapi/operations/docChecksLogsList';
import { JobLogResponse } from '../../openapi/types';
import { useDocsImportLogsListResource } from '../../openapi/operations/docsImportLogsList';
import { useOrdersLogsListResource } from '../../openapi/operations/ordersLogsList';

const msg = defineMessages({
  logListQueryStateWaiting: {
    id: 'logList.queryState.waiting',
    defaultMessage: 'Čekejte prosím...',
  },
  logListQueryStateFail: {
    id: 'logList.queryState.fail',
    defaultMessage: 'Záznamy se nepovedlo načíst. Zkuste to prosím později.',
  },
  logListEmpty: {
    id: 'logList.empty',
    defaultMessage: 'Žádné záznamy zatím neexistují.',
  },
  showMore: {
    id: 'logList.showMore',
    defaultMessage: 'Zobrazit starší protokoly ({count, plural, one {# záznam} few {# záznamy} other {# záznamů}})',
  },
  showLess: {
    id: 'logList.showLess',
    defaultMessage: 'Skrýt starší protokoly',
  },
});

type LogListProps = {
  objectId: number;
  resourceType: LogResourceType;
};

const resources: Record<LogResourceType, any> = {
  check: useDocChecksLogsListResource,
  doc: useDocsImportLogsListResource,
  dtm: useDocChecksLogsListResource,
  order: useOrdersLogsListResource,
};

export const LogList: React.FC<LogListProps> = React.memo(({ objectId, resourceType }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const logs = resources[resourceType](objectId ? { id: objectId } : null);

  const hasLogs = logs.data && logs.data.length > 0;
  const isExpandable = hasLogs && logs.data.length > 5;
  const visibleLogs = isExpanded ? logs.data : logs.data?.slice(0, 5);

  return (
    <>
      {hasLogs ? (
        <div className='sg-u-box sg-a-o-h dmx-a-mw-fc'>
          <Table>
            <tbody>
              {visibleLogs!.map((logItem: JobLogResponse) => (
                <LogListItem key={logItem.id} {...logItem} />
              ))}
            </tbody>
          </Table>

          {isExpandable && (
            <div className='sg-a-p-2 sg-a-d-f sg-a-jc-c'>
              <SecondaryBtn size="sml" icon={isExpanded ? rightIconUp : rightIconDown} onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? (
                  <FormattedMessage {...msg.showLess} />
                ) : (
                  <FormattedMessage {...msg.showMore} values={{ count: logs.data!.length - 5 }} />
                )}
              </SecondaryBtn>
            </div>
          )}
        </div>
      ) : (
        <div className='sg-u-box sg-a-p-2'>
          {logs.queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.logListQueryStateWaiting} />
            </WaitingAlert>
          )}
          {logs.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.logListQueryStateFail} />
            </DangerAlert>
          )}
          {logs.queryState === QUERY_STATE.SUCCESS && (
            <InfoAlert>
              <FormattedMessage {...msg.logListEmpty} />
            </InfoAlert>
          )}
        </div>
      )}
    </>
  );
});
